/**
 * @file App.jsx
 * @description Main application component with routing and authentication
 * @module App
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  AccountCircle,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useSnackbar } from 'notistack';

import Home from './pages/Home';
import Processing from './pages/Processing';
import Preview from './pages/Preview';
import Export from './pages/Export';
import Login from './pages/Login';
import AdminUsers from './pages/AdminUsers';
import AdminSettings from './pages/AdminSettings';
import ProtectedRoute from './components/ProtectedRoute';
import useAppStore from './context/AppContext';
import { logout as logoutApi } from './services/api';

/**
 * Header component with user menu
 */
function Header() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user, isAuthenticated, logout } = useAppStore();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    try {
      await logoutApi();
    } catch {
      // Ignore errors
    }
    logout();
    enqueueSnackbar('Logged out successfully', { variant: 'info' });
    navigate('/login');
  };

  const handleAdminClick = () => {
    handleMenuClose();
    navigate('/admin/users');
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/admin/settings');
  };

  return (
    <AppBar position="static" elevation={0}>
      <Toolbar>
        <DescriptionIcon sx={{ mr: 2 }} />
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, cursor: 'pointer' }}
          onClick={() => isAuthenticated && navigate('/')}
        >
          QBO CSV Generator
        </Typography>

        {isAuthenticated && user && (
          <>
            <Button
              color="inherit"
              startIcon={<AccountCircle />}
              onClick={handleMenuOpen}
              sx={{ textTransform: 'none' }}
            >
              {user.name || user.email}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem disabled>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={user.email}
                  secondary={user.role}
                />
              </MenuItem>
              <Divider />
              {user.role === 'ADMIN' && (
                <>
                  <MenuItem onClick={handleAdminClick}>
                    <ListItemIcon>
                      <AdminIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="User Management" />
                  </MenuItem>
                  <MenuItem onClick={handleSettingsClick}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Settings" />
                  </MenuItem>
                </>
              )}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

/**
 * Main App layout component
 */
function AppLayout() {
  const { isAuthenticated, authLoading } = useAppStore();

  // Don't show header/footer on login page or while loading
  const showLayout = isAuthenticated && !authLoading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {showLayout && <Header />}

      <Box component="main" sx={{ flex: 1 }}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Container sx={{ py: 4 }}>
                  <Home />
                </Container>
              </ProtectedRoute>
            }
          />
          <Route
            path="/processing"
            element={
              <ProtectedRoute>
                <Container sx={{ py: 4 }}>
                  <Processing />
                </Container>
              </ProtectedRoute>
            }
          />
          <Route
            path="/preview"
            element={
              <ProtectedRoute>
                <Container sx={{ py: 4 }}>
                  <Preview />
                </Container>
              </ProtectedRoute>
            }
          />
          <Route
            path="/export"
            element={
              <ProtectedRoute>
                <Container sx={{ py: 4 }}>
                  <Export />
                </Container>
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <AdminSettings />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>

      {showLayout && (
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            mt: 'auto',
            backgroundColor: 'grey.100',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Convert bank statements to QuickBooks Online format using AI
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * Root App component with auth initialization
 */
function App() {
  const { initAuth } = useAppStore();

  // Initialize auth state from localStorage on app load
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

export default App;
